import { useChangeSubscriptionPlan } from '@/hooks/actions/use-change-subscription-plan';
import { useCreateBillingCharge } from '@/hooks/actions/use-create-billing-charge';
import { useBillingActionScope } from '@/hooks/actions/use-billing-action-scope';
import { readBillingContext } from '@/libs/billing-access';
import { getLoginReturnPath } from '@/libs/mcp-return-to';
import { isSuccessFeeTermsRefusal } from '@/libs/utils/success-fee-terms-refusal';
import type {
  CreateOrganizationBillingChargeRequest,
  ChangeSubscriptionPlanRequest,
  OrganizationBillingChargeResult,
  OrganizationBillingContext,
  PlanChangeResponseData,
} from '@fluenceur/types';
import { useEffect, useRef, useState } from 'react';

export interface UsePlanCommitmentOptions {
  /** The organization already pays, so the commitment replaces its plan. */
  isChangeMode: boolean;
  /** Compatibility with a Shopify server whose context predates billing channels. */
  hasShopifyIntegration: boolean;
  /** That store is still being read. */
  isCheckingIntegration: boolean;
  /** Where a merchant without a store goes instead of Shopify. */
  onMissingIntegration: () => void;
  /** The current organization and brand's server billing decision. */
  billingContext?: OrganizationBillingContext;
  /** An internal navigation hint; completing Checkout never authorizes OAuth. */
  returnTo?: string;
  /** Explicit first-time payer collected by the existing billing onboarding. */
  identity?: CreateOrganizationBillingChargeRequest['identity'];
}

export interface PlanCommitment {
  /** Commits the organization to the selected monthly plan. */
  commit: (planId: string) => void;
  isCommitting: (planId: string) => boolean;
  /** The plan a merchant chose and cannot get until the terms are accepted. */
  planAwaitingTerms: string | null;
  /** The reader closed the terms without accepting; the plan is dropped. */
  dismissTerms: () => void;
  /** The acceptance is recorded; the very same plan is sent again. */
  resumeAfterAcceptance: () => void;
  /** A server receipt for the UI; only the canonical context establishes access. */
  chargeResult: OrganizationBillingChargeResult | null;
  planChangeResult: PlanChangeResponseData | null;
  checkPlanChange: () => void;
  isCheckingPlanChange: boolean;
  isPlanChangePending: boolean;
}

/**
 * Buying a plan, and the one refusal that stands between a merchant and it.
 *
 * Both surfaces run through here: the paywall creates a charge, the settings
 * page replaces a plan, and the API guards each of them with the same question
 * — has this organization agreed to the success fee its subscription will
 * carry?
 *
 * That question is asked of the API, never guessed here. Only the API knows
 * whether the charge about to be created enrols this merchant on the rail, and
 * a copy of that reasoning in the dashboard would eventually drift into asking
 * an existing customer to accept a second time, or into letting a new one
 * through without asking at all. So the refusal is the trigger: nothing is read
 * and nothing is shown until the API says the acceptance is missing, which
 * means an organization with nothing to accept sees the pricing page it has
 * always seen, down to the requests it makes.
 */
export function usePlanCommitment(options: UsePlanCommitmentOptions): PlanCommitment {
  const { isChangeMode, hasShopifyIntegration, isCheckingIntegration, onMissingIntegration } =
    options;

  const inFlight = useRef(false);
  const [isBusy, setIsBusy] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [planAwaitingTerms, setPlanAwaitingTerms] = useState<string | null>(null);
  const [chargeResult, setChargeResult] = useState<OrganizationBillingChargeResult | null>(null);
  const [localPlanChangeResult, setLocalPlanChangeResult] = useState<PlanChangeResponseData | null>(
    null
  );
  const { scope, assertCurrent } = useBillingActionScope();
  const termsTarget = useRef<typeof scope | null>(null);

  const createCharge = useCreateBillingCharge();
  const changePlan = useChangeSubscriptionPlan({ billingContext: options.billingContext });
  const planChangeResult = changePlan.planChangeResult ?? localPlanChangeResult;
  const isPlanChangePending =
    planChangeResult?.kind === 'requires_action' ||
    (planChangeResult?.kind === 'pending' &&
      !['expired', 'superseded'].includes(planChangeResult.status));

  useEffect(() => {
    inFlight.current = false;
    termsTarget.current = null;
    setIsBusy(false);
    setSelectedPlanId(null);
    setPlanAwaitingTerms(null);
    setChargeResult(null);
    setLocalPlanChangeResult(null);
  }, [scope.organizationId, scope.brandId, scope.userId]);

  const commit = (planId: string) => {
    if (isCheckingIntegration || inFlight.current || createCharge.isPending || changePlan.isPending)
      return;
    const context = options.billingContext;
    const isShopifyFree = context?.channel === 'shopify' && planId === 'free';
    if (context) {
      if (
        !readBillingContext(context) ||
        context.organizationId !== scope.organizationId ||
        context.brandId !== scope.brandId ||
        (isShopifyFree
          ? !context.shopifyFreeApproval?.canApprove ||
            !['required', 'pending'].includes(context.shopifyFreeApproval.requirement) ||
            ['reserved', 'processing', 'uncertain'].includes(
              context.shopifyFreeApproval.status ?? ''
            )
          : !context.capabilities.canCommitPaidPlan) ||
        context.cardSetup.exemption === 'impersonation'
      )
        return;
      if (isChangeMode && context.channel !== 'shopify') {
        if (
          context.channel !== 'stripe' ||
          context.subscription?.status !== 'active' ||
          !context.subscription.id ||
          context.taxQualificationRequired !== false ||
          !context.billingCurrency ||
          context.cardSetup.emailVerificationRequired ||
          isPlanChangePending ||
          !['essential_monthly', 'growth_monthly'].includes(planId)
        )
          return;
      }
      if (!isChangeMode && context.channel !== 'shopify') {
        if (
          context.channel !== 'stripe' ||
          !context.checkoutAvailability.paid ||
          context.taxQualificationRequired ||
          !context.billingCurrency ||
          context.cardSetup.emailVerificationRequired ||
          (context.cardSetup.identityRequired && !options.identity) ||
          !['essential_monthly', 'growth_monthly'].includes(planId)
        )
          return;
        const operation = context.paidCheckout;
        // Open operations may be resumed for the same plan. A confirmation is
        // checked with GET instead of another financial request.
        if (
          (operation &&
            operation.status !== 'expired' &&
            (operation.planId !== planId || operation.status !== 'open')) ||
          (chargeResult?.kind === 'pending' && !operation)
        )
          return;
      }
    }
    const target = scope;
    inFlight.current = true;
    setIsBusy(true);
    setSelectedPlanId(planId);

    // The acceptance is asked for once per plan chosen, so a merchant that
    // closed the terms and picked the other plan is asked again rather than
    // reopening on the plan it walked away from.
    setPlanAwaitingTerms(null);
    termsTarget.current = null;

    const onError = (error: unknown) => {
      try {
        assertCurrent(target);
      } catch {
        return;
      }
      inFlight.current = false;
      setIsBusy(false);
      // The only failure this page answers itself. Everything else is announced
      // by the mutation that raised it.
      if (isSuccessFeeTermsRefusal(error)) {
        termsTarget.current = target;
        setPlanAwaitingTerms(planId);
      }
    };

    if (isChangeMode && !isShopifyFree) {
      const payload: ChangeSubscriptionPlanRequest | string =
        context?.channel === 'stripe'
          ? {
              planId: planId as ChangeSubscriptionPlanRequest['planId'],
              expectedSubscriptionId: context.subscription!.id,
            }
          : planId;
      changePlan.mutate(payload, {
        onError,
        onSuccess: (result) => {
          try {
            assertCurrent(target);
          } catch {
            return;
          }
          if (!result || result.kind === 'redirect') return;
          inFlight.current = false;
          setIsBusy(false);
          setLocalPlanChangeResult(result);
        },
      });
      return;
    }

    if ((!context || context.channel === 'shopify') && !hasShopifyIntegration) {
      inFlight.current = false;
      setIsBusy(false);
      onMissingIntegration();
      return;
    }

    const returnTo = options.returnTo
      ? getLoginReturnPath(new URLSearchParams({ returnTo: options.returnTo }))
      : undefined;
    const payload: CreateOrganizationBillingChargeRequest | string = isShopifyFree
      ? { planId: 'free', consent: true }
      : context?.channel === 'stripe'
        ? {
            planId: planId as CreateOrganizationBillingChargeRequest['planId'],
            consent: true,
            ...(context.cardSetup.identityRequired ? { identity: options.identity } : {}),
            ...(returnTo ? { returnTo } : {}),
          }
        : planId;
    createCharge.mutate(payload, {
      onError,
      ...(context?.channel === 'stripe' || isShopifyFree
        ? {
            onSuccess: (result: OrganizationBillingChargeResult) => {
              try {
                assertCurrent(target);
              } catch {
                return;
              }
              // Legacy Shopify callbacks and redirects keep the button locked until
              // navigation. Pending/applied receipts release the request spinner.
              if (!result || result.kind === 'redirect') return;
              inFlight.current = false;
              setIsBusy(false);
              setChargeResult(result);
            },
          }
        : {}),
    });
  };

  return {
    commit,
    isCommitting: (planId) =>
      selectedPlanId === planId && (isBusy || createCharge.isPending || changePlan.isPending),
    planAwaitingTerms,
    chargeResult,
    planChangeResult,
    isPlanChangePending,
    checkPlanChange: () => {
      void changePlan.checkPlanChange?.();
    },
    isCheckingPlanChange: changePlan.isCheckingPlanChange ?? false,
    dismissTerms: () => {
      termsTarget.current = null;
      setPlanAwaitingTerms(null);
    },
    resumeAfterAcceptance: () => {
      if (!planAwaitingTerms) return;
      try {
        if (!termsTarget.current) return;
        assertCurrent(termsTarget.current);
      } catch {
        termsTarget.current = null;
        setPlanAwaitingTerms(null);
        return;
      }

      commit(planAwaitingTerms);
    },
  };
}
